import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../../../services/baseQuery';
import type { ApiResponse } from '../../menu/types';
import { toCamelCase } from '../utils';
import { extractErrorMessage } from '../../../utils/errorUtils';

const BANK_STATEMENT_IMPORT_API = '/umbraco/api/SubscriptionProofBankImport';

export const BANK_STATEMENT_IMPORT_STATUS = {
  matched: 'Matched',
  needReview: 'Need review',
  duplicate: 'Duplicate',
} as const;

export type BankStatementPreviewRow = {
  rowId: string;
  paymentDate: string | null;
  subscriptionName: string;
  infSubscriptionId: number | null;
  currency: string;
  crmCurrencyId: number | null;
  amount: number;
  description: string;
  status: string;
  selected: boolean;
};

export type SubscriptionOption = {
  infSubscriptionId: number;
  infSubscriptionName: string;
};

export type BankStatementPreview = {
  rows: BankStatementPreviewRow[];
  subscriptions: SubscriptionOption[];
  fileName: string;
  periodFrom: string | null;
  periodTo: string | null;
  totalCount: number;
  matchedCount: number;
  needReviewCount: number;
  duplicateCount: number;
};

export type BankStatementImportRequest = {
  nodeKey: string;
  rows: BankStatementPreviewRow[];
};

export type BankStatementImportResult = {
  message: string;
  importedCount: number;
  skippedCount: number;
  errors: string[];
};

const unwrapResponse = <T>(response: ApiResponse<T>, fallback: string): T => {
  if ((response.Code ?? response.code) === 1) {
    return toCamelCase(response.data) as T;
  }

  throw new Error(extractErrorMessage(response, fallback));
};

export const subscriptionProofBankImportApi = createApi({
  reducerPath: 'subscriptionProofBankImportApi',
  baseQuery,
  endpoints: (builder) => ({
    parseBankStatementPreview: builder.mutation<BankStatementPreview, { file: File }>({
      query: ({ file }) => {
        const body = new FormData();
        body.append('file', file);

        return {
          url: `${BANK_STATEMENT_IMPORT_API}/ParsePreview`,
          method: 'POST',
          body,
        };
      },
      transformResponse: (response: ApiResponse<BankStatementPreview>) =>
        unwrapResponse(response, 'Failed to parse bank statement.'),
    }),
    importBankStatement: builder.mutation<BankStatementImportResult, BankStatementImportRequest>({
      query: (request) => ({
        url: `${BANK_STATEMENT_IMPORT_API}/Import`,
        method: 'POST',
        body: request,
      }),
      transformResponse: (response: ApiResponse<BankStatementImportResult>) =>
        unwrapResponse(response, 'Failed to import bank statement.'),
    }),
  }),
});

export const { useParseBankStatementPreviewMutation, useImportBankStatementMutation } =
  subscriptionProofBankImportApi;
